import { formatMoney as formatDinero } from '@ems/money/format'
import { fromNumeric } from '@ems/money/money'

/**
 * The three formatters that were copy-pasted into five route files.
 *
 * Not a tidy-up. Each copy is a chance to disagree, and two of them already had: the admin
 * program and the portal's meetings list both render `time()` with the same options, while the
 * CMS version history calls a bare `toLocaleString()` — so the same instant reads
 * "21 Sep, 14:30" on one screen and "21/09/2026, 14:30:00" on the next, and an operator
 * cross-checking a meeting against its audit trail has to do the conversion in their head.
 *
 * **`Intl`, never a hand-rolled format.** The guidelines say so and the reason is concrete: this
 * product runs in Indonesian and English, for an event in `Asia/Jakarta`, billed in IDR. A
 * template string with a hardcoded separator gets the decimal comma wrong for half its audience
 * and the thousands separator wrong for the other half.
 */

/**
 * A timestamp, in the reader's own locale and zone.
 *
 * The event's zone travels in the instant itself — the API sends ISO-8601 with an offset — so a
 * delegate in Singapore sees their own clock, which is the one they will be looking at when they
 * decide whether they are late.
 *
 * `undefined` for the locale rather than a fixed tag: that is `Intl`'s "use the browser's", and
 * passing `[]` (as three of the copies did) means the same thing far less obviously.
 */
export function formatDateTime(instant: string, locale?: string): string {
  const at = Date.parse(instant)
  // The input is a string from an API. A bad one renders as itself rather than as "Invalid Date",
  // which at least tells somebody what arrived.
  if (Number.isNaN(at)) return instant

  return new Date(at).toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** A date with no time — a deadline, a release, a "last updated". */
export function formatDate(instant: string, locale?: string): string {
  const at = Date.parse(instant)
  if (Number.isNaN(at)) return instant

  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(new Date(at))
  } catch {
    // An unrecognized locale tag THROWS rather than falling back, and a `RangeError` here would
    // take a page down over a date. The locale comes from a CMS document, so it is a value an
    // editor can get wrong.
    return new Date(at).toISOString().slice(0, 10)
  }
}

/**
 * Money, in the currency the server said.
 *
 * **Formatting only — never arithmetic.** Every figure passed here was computed by the API, and a
 * total recomputed in the browser would be a second implementation of the billing invariants that
 * disagrees with the first on exactly the invoices somebody notices.
 *
 * The currency is a required argument rather than a default for the same reason: a tenant's ledger
 * currency is a property of the tenant, and a default would be a guess that renders IDR as dollars.
 *
 * Goes through Dinero (`@ems/money`) so the fraction digits are the platform's currency registry,
 * not ISO's — bare `Intl` renders whole-rupiah prices as `Rp 1.665.000,00`.
 */
export function formatMoney(amount: number, currency: string, locale?: string): string {
  try {
    return formatDinero(fromNumeric(String(amount), currency), locale)
  } catch {
    // A currency the registry does not declare, or an amount finer than its exponent. Showing the
    // number and the code is worse than a formatted total and much better than a blank page.
    return `${currency} ${amount.toLocaleString(locale)}`
  }
}
